import React from "react";
import { Link } from "react-router-dom";
import { InfoIcon } from "@phosphor-icons/react";
import { CustomBadge } from "../../shared/CustomBadge/CustomBadge";
import { ITestingProtocol } from "../../endpoints/profiling";
import styles from "./ProfilingHubPage.module.scss";
import classNames from "classnames";

const PROTOCOL_DESCRIPTIONS: Record<string, string> = {
  P1: "Profilo load-velocity arti inferiori (squat, deadlift, hip thrust)",
  P2: "Profilo load-velocity arti superiori (bench, OHP, pull-up)",
  P3: "Squat Jump incrementale → F0/V0/Pmax (Samozino)",
  P4: "Sprint distance-time → FH0/VH0/Pmax/DRF",
  P5: "Sled push/pull → Lopt (carico ottimale potenza)",
  P6: "Drop Jump multi-altezza → RSI ottimale + altezza ottimale",
  P7: "Isometric Mid-Thigh Pull → PF + RFD + DSI vs CMJ",
  P8: "Nordic Hamstring → Peak Force + asimmetria + break angle",
  P9: "Step lattato → LT1, LT2, zone HR aerobiche",
  P10: "VAMEVAL → VMA continua + zone %VMA",
  P11: "Gacon 45-15 → VMA intermittente",
  P12: "Yo-Yo IR1 → distanza + VO2max stimato",
  P13: "Yo-Yo IR2 → versione intensa",
  P14: "30-15 IFT → VIFT + zone HIIT",
};

interface ProtocolCardProps {
  protoId: string;
  protocol?: ITestingProtocol;
  isEnabled: boolean;
  lastProfile?: any;
  athleteId: string;
  /** Se fornito: chiama callback con protocolId invece di navigare con Link */
  onClick?: (protocolId: string) => void;
}

const ProtocolCard: React.FC<ProtocolCardProps> = ({
  protoId,
  protocol,
  isEnabled,
  lastProfile,
  athleteId,
  onClick,
}) => {
  const cardClassName = classNames(styles.protocolCard, {
    [styles.protocolCardEnabled]: isEnabled && !!protocol,
    [styles.protocolCardDisabled]: !isEnabled,
  });

  const cardContent = (
    <>
      <div className={styles.cardHeader}>
        <span className={styles.protoChip}>{protoId}</span>
        {isEnabled ? (
          <CustomBadge label="Attivo" color="lightgreen" />
        ) : (
          <CustomBadge label="In arrivo" color="lightblue" />
        )}
      </div>

      <p className={styles.cardTitleText}>
        {protocol?.name?.replace(/^P\d+\s*[—\-]\s*/, "") || "—"}
      </p>

      <p className={styles.cardDescription}>
        {PROTOCOL_DESCRIPTIONS[protoId]}
      </p>

      {lastProfile ? (
        <div className={styles.cardMetrics}>
          <div className={styles.metricChip}>
            <p className={styles.metricLabel}>Ultimo</p>
            <p className={styles.metricValue}>
              {new Date(lastProfile.sessionDate).toLocaleDateString("it-IT")}
            </p>
          </div>
          {Object.entries(lastProfile.outputs || {})
            .filter(
              ([k, v]) =>
                typeof v === "number" && !["n", "intercept", "slope"].includes(k)
            )
            .slice(0, 2)
            .map(([k, v]) => (
              <div key={k} className={styles.metricChip}>
                <p className={styles.metricLabel}>{k}</p>
                <p className={styles.metricValue}>{String(v)}</p>
              </div>
            ))}
        </div>
      ) : (
        isEnabled && (
          <div className={styles.emptyMetrics}>
            <InfoIcon size={14} color="#b5b5b5" />
            <p className={styles.emptyMetricsText}>Nessun profilo registrato</p>
          </div>
        )
      )}
    </>
  );

  if (!isEnabled || !protocol) {
    return <div className={cardClassName}>{cardContent}</div>;
  }

  if (onClick) {
    return (
      <button
        type="button"
        className={classNames(cardClassName, styles.protocolCardButton)}
        onClick={() => onClick(protocol._id)}
      >
        {cardContent}
      </button>
    );
  }

  return (
    <Link
      to={`/dashboard/users/${athleteId}/profiling/${protocol._id}`}
      className={cardClassName}
    >
      {cardContent}
    </Link>
  );
};

export default ProtocolCard;
